import { useAtomValue } from 'jotai';
import { useMemo } from 'react';
import { useTheme } from '@mui/material/styles';
import { SideDrawer } from '@/components/shared/SideDrawer';
import { CreatePoi } from '../Forms/CreatePoi';
import { PoiFormState } from '@/types';
import * as poiState from '../../../state';
import { UpdateGeneralFields } from '../Forms/UpdateGeneralFields/UpdateGeneralFields';
import { UpdateTemplateFields } from '../Forms/UpdateTemplateFields';
import { UpdateSectionFields } from '../Forms/UpdateSectionFields/UpdateSectionFields';
import { VerifyTemplateFields } from '../Forms/VerifyTemplateFields/VerifyTemplateFields';
import { useDrawerWidths } from '@/hooks/useDrawerWidths';
import { VoiCreation } from '../Forms/VoiCreation';

type PoiFormSelectorProps = {
  formState: PoiFormState | undefined;
};

const PoiFormSelector = ({ formState }: PoiFormSelectorProps) => {
  switch (formState) {
    case 'Create':
      return <CreatePoi />;
    case 'Update-General':
      return <UpdateGeneralFields />;
    case 'Update-Template':
      return <UpdateTemplateFields />;
    case 'Update-Section':
      return <UpdateSectionFields />;
    case 'Verify':
      return <VerifyTemplateFields />;
    case 'Resizing-VOI':
      return <VoiCreation />;
    default:
      return <></>;
  }
};

type Props = {
  open: boolean;
  defaultDrawerWidth?: number;
};
export const PoiEditingForms = ({ open, defaultDrawerWidth }: Props) => {
  const editPointOfInterest = useAtomValue(poiState.editPointOfInterest);
  const theme = useTheme();

  const pointOfInterest = editPointOfInterest?.pointOfInterest;

  const { rightDrawerWidth, doublePanelWidth } = useDrawerWidths();

  // Sits above ReviewScreen's ImagePreviewDrawer (zIndex.drawer + 2) so edit forms
  // launched from inside that drawer are not hidden behind it.
  const editFormZIndex = theme.zIndex.drawer + 3;

  const drawerWidth = useMemo(() => {
    if (
      editPointOfInterest.formState === 'Update-General' ||
      editPointOfInterest.formState === 'Update-Section' ||
      editPointOfInterest.formState === 'Verify'
    )
      return defaultDrawerWidth || rightDrawerWidth;

    if (pointOfInterest?.blister && editPointOfInterest.state === 'WaitingForDetail') {
      return doublePanelWidth;
    }

    return defaultDrawerWidth || rightDrawerWidth;
  }, [
    defaultDrawerWidth,
    doublePanelWidth,
    rightDrawerWidth,
    editPointOfInterest.formState,
    editPointOfInterest.state,
    pointOfInterest?.blister,
  ]);

  return (
    <SideDrawer
      position="right"
      width={drawerWidth || rightDrawerWidth}
      isOpen={open}
      handleOpen={() => {}}
      variant="persistent"
      scrollable
      sx={{ zIndex: editFormZIndex }}
      sxPaper={{ zIndex: editFormZIndex }}
    >
      <PoiFormSelector formState={editPointOfInterest.formState} />
    </SideDrawer>
  );
};
